import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/Api';
import { getSubscriptionPaymentId, hasAcknowledgedSubscriptionPayment, acknowledgeSubscriptionPayment } from '../utils/subscriptionPaymentConfirmation';

const emptyStatus = { isOwing: false, amountOwed: 0, dueDate: null, lastPaid: null };
export function useSubscriptionDialogs(user) {
  const { pathname } = useLocation();
  const eligible = user?.role === 'admin' && pathname.replace(/\/+$/, '') === '/dashboard';
  const [subscriptionStatus, setSubscriptionStatus] = useState(emptyStatus);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [showPaymentConfirmedDialog, setShowPaymentConfirmedDialog] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setShowSubscriptionDialog(false);
    setShowPaymentConfirmedDialog(false);
    if (!eligible) return;
    const load = async () => {
      try {
        const { data } = await api.get('/school/subscription-status');
        if (cancelled || !data?.success) return;
        setSubscriptionStatus(data);
        if (data.isOwing) {
          const schoolId = user.school?._id || user.school?.id || user.school;
          const key = `subscriptionWarningShown:${schoolId}:${user._id || user.id}`;
          try {
            if (!sessionStorage.getItem(key)) {
              setShowSubscriptionDialog(true);
              sessionStorage.setItem(key, 'true');
            }
          } catch { /* Keep the billing dashboard available when storage is blocked. */ }
          return;
        }
        const paymentId = getSubscriptionPaymentId(data.lastPaid);
        if (!paymentId) return;
        const alreadySeen = hasAcknowledgedSubscriptionPayment(data.lastPaid, user);
        // Also migrate existing browser acknowledgements to durable server storage.
        const response = await api.post(`/school/subscription-payments/${encodeURIComponent(paymentId)}/acknowledge`);
        if (cancelled) return;
        acknowledgeSubscriptionPayment(data.lastPaid, user);
        if (response.data?.shouldShow && !alreadySeen) setShowPaymentConfirmedDialog(true);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [eligible, user]);

  return {
    subscriptionStatus,
    showSubscriptionDialog: eligible && showSubscriptionDialog,
    showPaymentConfirmedDialog: eligible && showPaymentConfirmedDialog,
    closeSubscriptionDialog: () => setShowSubscriptionDialog(false),
    closePaymentConfirmedDialog: () => setShowPaymentConfirmedDialog(false)
  };
}
